<template>
  <div>
    <!-- Header -->
    <b-card no-body>
      <b-card-header>
        <b-card-title>{{ $t('serviceActions.title') }}</b-card-title>
        <b-button
          variant="primary"
          :to="{ name: 'service-actions-create' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">{{ $t('serviceActions.actions.add_action') }}</span>
        </b-button>
      </b-card-header>

      <!-- Filters -->
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('serviceActions.filters.service')"
              label-for="service"
            >
              <v-select
                v-model="filters.service_id"
                :options="serviceOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('serviceActions.placeholders.all_services')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('serviceActions.filters.responsibility_type')"
              label-for="responsibility"
            >
              <v-select
                v-model="filters.responsibility_type"
                :options="responsibilityOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group
              :label="$t('serviceActions.filters.status')"
              label-for="status"
            >
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table -->
    <b-card
      no-body
      class="mt-2"
    >
      <b-table
        ref="refServiceActionsTable"
        :items="serviceActions"
        :fields="tableColumns"
        :busy="false"
        responsive
        show-empty
        :empty-text="$t('serviceActions.messages.no_actions_found')"
        class="position-relative"
      >
        <!-- Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <!-- Service -->
        <template #cell(service)="data">
          <div v-if="data.item.service">
            <div><strong>{{ data.item.service.name || '-' }}</strong></div>
            <small
              v-if="data.item.service.code"
              class="text-muted"
            >{{ data.item.service.code }}</small>
          </div>
          <span v-else>-</span>
        </template>

        <!-- Name -->
        <template #cell(name)="data">
          <div>
            <strong>{{ data.item.name }}</strong>
            <p
              v-if="data.item.description"
              class="mb-0 text-muted small"
            >
              {{ data.item.description }}
            </p>
          </div>
        </template>

        <!-- Responsibility Type -->
        <template #cell(default_responsibility_type)="data">
          <b-badge
            v-if="data.item.default_responsibility_type"
            :variant="getResponsibilityVariant(data.item.default_responsibility_type)"
            pill
          >
            {{ formatResponsibilityType(data.item.default_responsibility_type) }}
          </b-badge>
          <span v-else>-</span>
        </template>

        <!-- Estimated Hours -->
        <template #cell(estimated_hours)="data">
          <span v-if="data.item.estimated_hours">
            {{ data.item.estimated_hours }} {{ $t('serviceActions.table.hrs') }}
          </span>
          <span v-else>-</span>
        </template>

        <!-- Estimated Cost -->
        <template #cell(estimated_cost)="data">
          <span v-if="data.item.estimated_cost">
            ${{ Number(data.item.estimated_cost).toLocaleString() }}
          </span>
          <span v-else>-</span>
        </template>

        <!-- Status -->
        <template #cell(is_active)="data">
          <b-badge
            :variant="data.item.is_active ? 'success' : 'danger'"
            pill
            class="cursor-pointer"
            @click="toggleStatus(data.item)"
          >
            {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
          </b-badge>
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              variant="flat-info"
              size="sm"
              :to="{ name: 'service-actions-edit', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-card-body class="d-flex justify-content-between align-items-center pt-0">
        <div>
          <span class="text-muted">{{ $t('common.showing') }} {{ meta.from || 0 }} {{ $t('common.to') }} {{ meta.to || 0 }} {{ $t('common.of') }} {{ meta.total || 0 }} {{ $t('common.entries') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total"
          :per-page="filters.per_page"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  formatResponsibilityType,
  getResponsibilityVariant,
} from '@/utils/responsibilityType'

export default {
  name: 'ServiceActionsList',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    vSelect,
  },
  data() {
    return {
      filters: {
        service_id: null,
        responsibility_type: null,
        is_active: null,
        sort_by: 'order',
        sort_direction: 'asc',
        per_page: 15,
      },
      currentPage: 1,
      tableColumns: [],
      serviceOptions: [],
      responsibilityOptions: [],
      statusOptions: [],
    }
  },
  computed: {
    ...mapState('serviceActions', ['serviceActions', 'meta', 'loading']),
  },
  created() {
    this.initializeOptions()
    this.fetchData()
    this.loadServices()

    // Check if service_id is in query params
    if (this.$route.query.service_id) {
      this.filters.service_id = parseInt(this.$route.query.service_id, 10)
    }
  },
  methods: {
    ...mapActions('serviceActions', [
      'fetchServiceActions',
      'deleteServiceAction',
      'toggleActionStatus',
    ]),
    ...mapActions('services', ['fetchServices']),
    initializeOptions() {
      this.tableColumns = [
        { key: 'order', label: this.$t('serviceActions.table.order'), sortable: true },
        { key: 'service', label: this.$t('serviceActions.table.service') },
        { key: 'name', label: this.$t('serviceActions.table.action') },
        { key: 'default_responsibility_type', label: this.$t('serviceActions.table.type') },
        { key: 'estimated_hours', label: this.$t('serviceActions.table.est_hours') },
        { key: 'estimated_cost', label: this.$t('serviceActions.table.est_cost') },
        { key: 'is_active', label: this.$t('serviceActions.table.status') },
        { key: 'actions', label: this.$t('common.actions') },
      ]
      this.responsibilityOptions = [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('serviceActions.responsibility_types.required'), value: 'required' },
        { label: this.$t('serviceActions.responsibility_types.optional'), value: 'optional' },
        { label: this.$t('serviceActions.responsibility_types.client_responsibility'), value: 'client_responsibility' },
      ]
      this.statusOptions = [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ]
    },
    async fetchData() {
      try {
        const params = {
          ...this.filters,
          page: this.currentPage,
        }
        await this.fetchServiceActions(params)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('serviceActions.messages.load_error'),
          },
        })
      }
    },
    async loadServices() {
      try {
        const response = await this.fetchServices({ all: true })
        this.serviceOptions = [
          { label: this.$t('serviceActions.placeholders.all_services'), value: null },
          ...response.data.map(s => ({
            label: `${s.name} (${s.service_code})`,
            value: s.id,
          })),
        ]
      } catch (error) {
        console.error('Failed to load services:', error)
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    async toggleStatus(item) {
      this.$swal({
        title: this.$t('serviceActions.messages.confirm_delete'),
        text: item.is_active ? this.$t('serviceActions.messages.confirm_deactivate') : this.$t('serviceActions.messages.confirm_activate'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('serviceActions.messages.yes'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.toggleActionStatus(item.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('serviceActions.messages.status_update_success'),
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || this.$t('serviceActions.messages.update_error'),
              },
            })
          }
        }
      })
    },
    confirmDelete(item) {
      this.$swal({
        title: this.$t('serviceActions.messages.confirm_delete'),
        text: this.$t('serviceActions.messages.confirm_delete_text'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('serviceActions.messages.yes_delete'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await this.deleteServiceAction(item.id)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.success'),
                icon: 'CheckIcon',
                variant: 'success',
                text: this.$t('serviceActions.messages.delete_success'),
              },
            })
            await this.fetchData()
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('common.error'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: error.response?.data?.message || this.$t('serviceActions.messages.delete_error'),
              },
            })
          }
        }
      })
    },
    getResponsibilityVariant(type) {
      return getResponsibilityVariant(type)
    },
    formatResponsibilityType(type) {
      return formatResponsibilityType(type)
    },
  },
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
